import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import api from '../api/api';
import config from '../config/environment';
import QRCode from 'react-qr-code';
import './PaymentPage.css';

// Stop polling after 15 minutes to protect the ICICI daily API quota
const MAX_POLL_MS = 15 * 60 * 1000;

const PaymentPage = () => {
  const { transactionId } = useParams();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUPI, setSelectedUPI] = useState('');
  const [customUPI, setCustomUPI] = useState('');
  const [redirectCountdown, setRedirectCountdown] = useState(3);

  const pollIntervalRef = useRef(null);
  const pollStartRef = useRef(null);

  const upiProviders = [
    { id: 'phonepe', name: 'PhonePe', logo: '/images/upi-apps/phonepe.svg' },
    { id: 'googlepay', name: 'Google Pay', logo: '/images/upi-apps/googlepay.svg' },
    { id: 'paytm', name: 'Paytm', logo: '/images/upi-apps/paytm.svg' },
    { id: 'amazonpay', name: 'Amazon Pay', logo: '/images/upi-apps/amazonpay.svg' },
    { id: 'bhim', name: 'BHIM UPI', logo: '/images/upi-apps/bhim.svg' }
  ];

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  useEffect(() => {
    pollStartRef.current = Date.now();
    fetchPaymentDetails();

    // Poll for payment status every 5 seconds
    pollIntervalRef.current = setInterval(fetchPaymentDetails, 5000);
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId]);

  const fetchPaymentDetails = async () => {
    // Stop polling after the max duration to protect the ICICI daily quota
    if (pollStartRef.current && (Date.now() - pollStartRef.current) > MAX_POLL_MS) {
      stopPolling();
      return;
    }

    try {
      console.log('Fetching payment details for:', transactionId);
      const response = await axios.get(`/api/payment/status/${transactionId}`);
      console.log('Payment response:', response.data);
      if (response.data.success) {
        setPayment(response.data.data);
        setLoading(false);
        
        // Stop polling if payment is completed (any terminal status)
        if (response.data.data.status !== 'PENDING') {
          stopPolling();
        }
      } else {
        // Payment not found - it will never resolve, so stop polling
        stopPolling();
        setError('Payment not found');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching payment:', error);
      // Persistently failing / nonexistent transaction - stop polling
      stopPolling();
      setError('Payment not found: ' + (error.response?.data?.message || error.message));
      setLoading(false);
    }
  };

  const handleUPIAppClick = (provider) => {
    const upiUrl = generateUPIUrl();
    
    // Try to open UPI app
    window.location.href = upiUrl;
    
    // Fallback: show instructions
    setTimeout(() => {
      alert(`Please open ${provider.name} app and complete the payment`);
    }, 1000);
  };

  const handleCustomUPISubmit = async (e) => {
    e.preventDefault();
    if (!customUPI) return;

    try {
      // Send payment request to custom UPI ID
      await axios.post('/api/payment/upi-request', {
        transactionId,
        upiId: customUPI
      });
      
      alert(`Payment request sent to ${customUPI}. Please check your UPI app.`);
    } catch (error) {
      alert('Failed to send payment request');
    }
  };

  const generateUPIUrl = () => {
    if (!payment) return '';
    
    return `upi://pay?pa=merchant@upi&am=${payment.amount}&tr=${payment.transactionId}&tn=${payment.description || 'Payment'}&cu=INR`;
  };

  const handleDummyPayment = async (status) => {
    try {
      const response = await axios.post(`/api/payment/simulate/${transactionId}`, {
        status: status
      });
      
      if (response.data.success) {
        // Refresh payment details
        await fetchPaymentDetails();
      }
    } catch (error) {
      console.error('Error simulating payment:', error);
      alert('Failed to simulate payment. This is a demo feature.');
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card loading-card">
          <div className="loading-spinner"></div>
          <h2>Loading Payment Details...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="card error-card">
          <h2 className="error-title">❌ Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="container">
        <div className="card text-center" role="status" aria-live="polite">
          <h2>Loading...</h2>
          <p>Fetching payment details...</p>
        </div>
      </div>
    );
  }

  if (payment.status === 'SUCCESS') {
    return <PaymentSuccess 
      payment={payment} 
      formatAmount={formatAmount} 
      redirectCountdown={redirectCountdown}
      setRedirectCountdown={setRedirectCountdown}
    />;
  }

  if (payment.status === 'FAILED') {
    const failureUrl = payment.callbackUrl
      ? `${payment.callbackUrl}${payment.callbackUrl.includes('?') ? '&' : '?'}status=failure&transactionId=${payment.transactionId}`
      : null;
    return (
      <div className="container">
        <div className="card error-card">
          <h2 className="error-title">❌ Payment Failed</h2>
          <p><strong>Transaction ID:</strong> {payment.transactionId}</p>
          <p>Please try again or contact support.</p>
          {failureUrl ? (
            <button
              className="btn btn-primary"
              onClick={() => window.location.href = failureUrl}
            >
              Return to {new URL(payment.callbackUrl).hostname}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              Retry Payment
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card payment-container">
        <div className="payment-header">
          <h2>Complete Your Payment</h2>
          <div className="payment-amount">
            {formatAmount(payment.amount)}
          </div>
          <p><strong>Transaction ID:</strong> {payment.transactionId}</p>
        </div>

        {/* UPI App Options */}
        <div className="payment-section">
          <h3>Pay with UPI Apps</h3>
          <div className="upi-providers-grid">
            {upiProviders.map((provider) => (
              <button
                key={provider.id}
                className="upi-provider-card"
                onClick={() => handleUPIAppClick(provider)}
                aria-label={`Pay with ${provider.name}`}
              >
                <img 
                  src={provider.logo} 
                  alt={provider.name}
                  className="upi-provider-logo"
                />
                <div className="upi-provider-name">
                  {provider.name}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* QR Code */}
        <div className="payment-section qr-code-section">
          <h3>Scan QR Code</h3>
          <p className="qr-code-instructions">Scan with any UPI app to pay</p>
          <div className="qr-code-container">
            <QRCode 
              value={generateUPIUrl()}
              size={200}
            />
          </div>
        </div>

        {/* Custom UPI ID */}
        <div className="payment-section custom-upi-section">
          <h3>Pay with UPI ID</h3>
          <form onSubmit={handleCustomUPISubmit}>
            <label htmlFor="custom-upi-id" className="sr-only">Enter UPI ID</label>
            <input
              id="custom-upi-id"
              type="text"
              className="custom-upi-input"
              placeholder="Enter UPI ID (e.g., user@paytm)"
              value={customUPI}
              onChange={(e) => setCustomUPI(e.target.value)}
              aria-label="Enter UPI ID"
            />
            <button type="submit" className="btn btn-success" style={{ width: '100%' }}>
              Send Payment Request
            </button>
          </form>
        </div>

        {/* Payment Status */}
        <div className="payment-status-card">
          <p><strong>Status:</strong> {payment.status}</p>
          <p>This page will automatically update when payment is completed.</p>
        </div>

        {/* Demo/Testing Buttons */}
        <div className="demo-section">
          <h2 className="demo-title">🧪 Demo Mode - Simulate Payment</h2>
          <p className="demo-description">
            For testing purposes, you can simulate payment success or failure
          </p>
          <div className="demo-buttons">
            <button 
              className="demo-button success"
              onClick={() => handleDummyPayment('SUCCESS')}
            >
              ✅ Simulate Success
            </button>
            <button 
              className="demo-button danger"
              onClick={() => handleDummyPayment('FAILED')}
            >
              ❌ Simulate Failure
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Payment Success Component with Redirect
const PaymentSuccess = ({ payment, formatAmount, redirectCountdown, setRedirectCountdown }) => {
  useEffect(() => {
    const timer = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev <= 1) {
          // Redirect to merchant's callbackUrl if provided, otherwise fallback to test merchant
          const redirectUrl = payment.callbackUrl
            ? `${payment.callbackUrl}${payment.callbackUrl.includes('?') ? '&' : '?'}status=success&transactionId=${payment.transactionId}&amount=${payment.amount}`
            : `${config.testMerchantUrl}?transactionId=${payment.transactionId}&amount=${payment.amount}&status=${payment.status}`;
          window.location.href = redirectUrl;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [setRedirectCountdown]);

  const handleManualRedirect = () => {
    const redirectUrl = payment.callbackUrl
      ? `${payment.callbackUrl}${payment.callbackUrl.includes('?') ? '&' : '?'}status=success&transactionId=${payment.transactionId}&amount=${payment.amount}`
      : `${config.testMerchantUrl}?transactionId=${payment.transactionId}&amount=${payment.amount}&status=${payment.status}`;
    window.location.href = redirectUrl;
  };

  return (
    <div className="container">
      <div className="card success-card">
        <div className="success-animation">
          <div className="checkmark-circle">
            <div className="checkmark">✓</div>
          </div>
        </div>
        
        <h2 className="success-title">Payment Successful!</h2>
        
        <div className="payment-details">
          <p><strong>Transaction ID:</strong> {payment.transactionId}</p>
          <p><strong>Amount:</strong> {formatAmount(payment.amount)}</p>
          <p className="success-message">Thank you for your payment!</p>
        </div>

        <div className="redirect-section">
          <div className="countdown-circle">
            <span className="countdown-number">{redirectCountdown}</span>
          </div>
          <p className="redirect-message">
            Redirecting to merchant application in {redirectCountdown} seconds...
          </p>
          
          <button 
            className="btn btn-primary redirect-button"
            onClick={handleManualRedirect}
          >
            Return to Merchant App Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;